import { makeOptionallyAuthenticatedQuery } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type {
  SimilarSearchRequestDto,
  SimilarSearchResponse,
} from '@b-stock/search-api-client'

import getAllListingsSearchClient from './getAllListingsSearchClient'
import type { SearchResult } from './listingSearchQuery'
import { augmentListings } from './listingSearchQuery'

export const SIMILAR_SEARCH_LIMIT = 19

export type SimilarSearchResult = Omit<SimilarSearchResponse, 'listings'> & {
  listings: SearchResult[]
}

/**
 * Raw similar-search fetch, without the per-listing `augmentListings`
 * enrichment `getSimilarSearchData` normally does. Exists so a caller that
 * fans out across many seeds (recentViewsSeedCandidatesQuery) can dedupe
 * first and augment the merged result set once, instead of augmenting every
 * seed's raw results before some of them are excluded/deduped away.
 */
export const fetchSimilarListings = async (
  params: SimilarSearchRequestDto,
  axiosParams: ReturnType<typeof buildAxiosRequestConfig>
): Promise<SimilarSearchResponse | undefined> => {
  const client = getAllListingsSearchClient()
  try {
    const { data } = await client.similarSearch(
      { similarSearchRequestDto: params },
      axiosParams
    )
    return data
  } catch {
    // Fail silently — no error shown to buyer per spec
    return undefined
  }
}

export const getSimilarSearchData = async (
  accessToken: string | null,
  params: SimilarSearchRequestDto
): Promise<SimilarSearchResult | undefined> => {
  const axiosParams = buildAxiosRequestConfig({ accessToken })
  const data = await fetchSimilarListings(params, axiosParams)
  if (!data) return undefined

  try {
    const listings = await augmentListings(
      accessToken,
      data.listings,
      axiosParams
    )
    return { ...data, listings }
  } catch {
    // Fail silently — no error shown to buyer per spec. fetchSimilarListings
    // guards its own call; this guards augmentListings, which used to share
    // one try/catch with it before the two were split apart.
    return undefined
  }
}

const similarSearchQuery = makeOptionallyAuthenticatedQuery<
  [SimilarSearchRequestDto],
  SimilarSearchResult | undefined
>('similar-search', getSimilarSearchData, {
  staleTime: 5 * 60 * 1000, // 5 minutes
})

export default similarSearchQuery
